import React, { useEffect, useState, useMemo } from 'react';
import {
  fetchRecordViewConfigRows,
  fetchObjectMetaBatch,
  configRowsToMapByObject,
  formatRecordViewLayoutCells,
} from './recordViewLayoutRoleApi';

/**
 * Read-only summary of role.permission_config.recordviewsByObject for the permission view page.
 */
const RecordViewLayoutTableSection = ({ roleId, permissions }) => {
  const [loading, setLoading] = useState(true);
  const [displayRows, setDisplayRows] = useState([]);

  const objectNamesKey = useMemo(
    () =>
      (permissions || [])
        .map((p) => p.object_name)
        .filter(Boolean)
        .join('\0'),
    [permissions]
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!roleId || !permissions?.length) {
        setDisplayRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const objectNames = [...new Set(permissions.map((p) => p.object_name).filter(Boolean))];
        const [configRows, metaByObject] = await Promise.all([
          fetchRecordViewConfigRows(roleId),
          fetchObjectMetaBatch(objectNames),
        ]);
        const cfgMap = configRowsToMapByObject(configRows);
        const rows = permissions.map((p) => {
          const on = p.object_name;
          const cfg = cfgMap[on] || null;
          const meta = metaByObject[on] || { recordviews: [], layouts: [] };
          const cells = formatRecordViewLayoutCells(cfg, meta);
          return {
            key: p.id || on,
            objectLabel: p.object_display_name || on,
            isStandard: p.is_standard === true,
            metaError: meta.error,
            ...cells,
          };
        });
        if (!cancelled) setDisplayRows(rows);
      } catch (e) {
        if (!cancelled) setDisplayRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [roleId, objectNamesKey, permissions]);

  if (!permissions?.length) {
    return null;
  }

  const standardRows = displayRows.filter((r) => r.isStandard);
  const customRows = displayRows.filter((r) => !r.isStandard);

  const renderBody = (rows) =>
    rows.map((r) => (
      <tr key={r.key}>
        <td className="object-cell">
          <div className="object-name">{r.objectLabel}</div>
        </td>
        <td className="recordview-layout-value-cell">{r.recordViews}</td>
        <td className="recordview-layout-value-cell">{r.detailLayout}</td>
        <td className="recordview-layout-value-cell">{r.editLayout}</td>
      </tr>
    ));

  return (
    <div className="recordview-layout-subsection">
      <div className="permissions-header">
        <h3>Record views &amp; default layouts</h3>
      </div>
      <p className="permissions-tabs-summary recordview-layout-summary">
        Read-only summary of assignments for this role. To change values, use{' '}
        <strong>Assign Record View/Layout</strong>.
      </p>
      {loading ? (
        <div className="permissions-tabs-summary">Loading…</div>
      ) : (
        <div className="permissions-table-container recordview-layout-matrix">
          <table className="permissions-table">
            <thead>
              <tr>
                <th className="object-column text-dark">Object name</th>
                <th className="text-dark">Record view</th>
                <th className="text-dark">Detail layout</th>
                <th className="text-dark">Edit layout</th>
              </tr>
            </thead>
            <tbody>
              {standardRows.length > 0 && (
                <>
                  <tr className="permission-section-header">
                    <td colSpan="4" className="section-header-cell">
                      Standard Objects
                    </td>
                  </tr>
                  {renderBody(standardRows)}
                </>
              )}
              {customRows.length > 0 && (
                <>
                  <tr className="permission-section-header">
                    <td colSpan="4" className="section-header-cell">
                      Custom Objects
                    </td>
                  </tr>
                  {renderBody(customRows)}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecordViewLayoutTableSection;
